import React, { useState, useEffect } from 'react';
import { Field } from 'formik';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const Paiement = ({ formikProps }) => {
  const { values, errors, touched, setFieldValue } = formikProps;
  const { user } = useAuth();
  const [parametres, setParametres] = useState(null);
  const [hasAdhesionN1, setHasAdhesionN1] = useState(false);
  const [loading, setLoading] = useState(true);

  // Récupérer les paramètres de tarifs pour l'organisme et l'année sélectionnée
  useEffect(() => {
    const fetchParametres = async () => {
      try {
        const organisme = values.organisme;
        const annee = values.annee;
        
        if (!organisme || !annee) return;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/parametres/${organisme}/${annee}`
        );
        setParametres(response.data);

        // Vérifier si l'utilisateur a une adhésion active de l'année N-1 pour la SAR
        if (organisme === 'SAR' && user) {
          try {
            const adhesionsResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/adhesions/user`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            const adhesionN1 = adhesionsResponse.data.find(
              adh => adh.organisme === 'SAR' && 
                     adh.annee === annee - 1 && 
                     (adh.status === 'actif' || adh.status === 'expiree')
            );
            
            setHasAdhesionN1(!!adhesionN1);
          } catch (error) {
            console.error('Erreur lors de la vérification de l\'adhésion N-1:', error);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des paramètres:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchParametres();
  }, [values.organisme, values.annee, user]);

  // Calculer le montant pour chaque organisme
  const calculateMontantSAR = () => {
    if (!parametres || !parametres.tarifs || !parametres.tarifs.SAR) return 0;
    
    const cotisationBase = parametres.tarifs.SAR.base;
    const droitEntree = hasAdhesionN1 ? 0 : parametres.tarifs.SAR.droitEntree;
    const nombreRuches = values.informationsApicoles?.nombreRuches || 0;
    const cotisationRuches = nombreRuches * parametres.tarifs.SAR.cotisationParRuche;
    
    return cotisationBase + droitEntree + cotisationRuches;
  };

  const calculateMontantAMAIR = () => {
    if (!parametres || !parametres.tarifs || !parametres.tarifs.AMAIR) return 0;
    // Si l'utilisateur est adhérent SAR, l'adhésion AMAIR est gratuite
    const isAdherentSAR = values.informationsSpecifiques?.AMAIR?.adherentSAR;
    if (isAdherentSAR) return 0;
    return parametres.tarifs.AMAIR.base;
  };

  const getMontantByOrganisme = (organisme) => {
    if (organisme === 'SAR') {
      return calculateMontantSAR();
    } else if (organisme === 'AMAIR') {
      return calculateMontantAMAIR();
    }
    return 0;
  };

  const getTotalMontant = () => {
    const organisme = values.organisme;
    return getMontantByOrganisme(organisme);
  };

  // Mettre à jour le montant dans le formulaire quand il change
  useEffect(() => {
    if (!loading && parametres) {
      const montant = getTotalMontant();
      setFieldValue('paiement.montant', montant);
      
      // Si le montant est 0 (adhérent SAR pour AMAIR), définir le type de paiement comme gratuit
      if (montant === 0 && organisme === 'AMAIR') {
        setFieldValue('paiement.typePaiement', 'gratuit');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametres, hasAdhesionN1, values.informationsApicoles?.nombreRuches, values.informationsSpecifiques?.AMAIR?.adherentSAR, loading]);

  const getOrganismeFullName = (organisme) => {
    return organisme === 'SAR' 
      ? 'Syndicat Apicole de la Réunion' 
      : 'Association de la Maison de l\'Apiculture de l\'Île de la Réunion';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Chargement des tarifs...</div>
      </div>
    );
  }

  if (!parametres) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">Erreur lors du chargement des tarifs. Veuillez réessayer.</p>
      </div>
    );
  }

  const organisme = values.organisme;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Paiement
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Récapitulatif et choix du mode de paiement
        </p>
      </div>

      {/* Récapitulatif de l'adhésion */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Récapitulatif de votre adhésion</h4>
        
        {organisme ? (
          <div className="space-y-4">
            <div className="bg-white rounded-md p-4 border border-gray-200">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h5 className="font-semibold text-gray-900">{organisme}</h5>
                  <p className="text-sm text-gray-600">{getOrganismeFullName(organisme)}</p>
                  <p className="text-sm text-gray-500">Année {values.annee}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-indigo-600">
                    {getMontantByOrganisme(organisme).toFixed(2)} €
                  </p>
                </div>
              </div>
              
              {/* Détail du calcul pour SAR */}
              {organisme === 'SAR' && parametres.tarifs.SAR && (
                <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                  <p>• Cotisation de base : {parametres.tarifs.SAR.base.toFixed(2)} €</p>
                  <p>• Droit d'entrée : {hasAdhesionN1 ? '0,00 € (exonéré - adhérent N-1)' : `${parametres.tarifs.SAR.droitEntree.toFixed(2)} €`}</p>
                  <p>• Cotisation par ruche ({values.informationsApicoles?.nombreRuches || 0} ruches × {parametres.tarifs.SAR.cotisationParRuche.toFixed(2)} €) : {((values.informationsApicoles?.nombreRuches || 0) * parametres.tarifs.SAR.cotisationParRuche).toFixed(2)} €</p>
                </div>
              )}
              
              {/* Détail pour AMAIR */}
              {organisme === 'AMAIR' && parametres.tarifs.AMAIR && (
                <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                  {values.informationsSpecifiques?.AMAIR?.adherentSAR ? (
                    <>
                      <p>• Cotisation fixe : <span className="line-through">{parametres.tarifs.AMAIR.base.toFixed(2)} €</span></p>
                      <p className="text-green-600 font-medium">• Adhérent SAR : Gratuit (0,00 €)</p>
                    </>
                  ) : (
                    <p>• Cotisation fixe : {parametres.tarifs.AMAIR.base.toFixed(2)} €</p>
                  )}
                </div>
              )}
            </div>

            {/* Adhésion AMAIR gratuite pour adhérents SAR */}
            {organisme === 'SAR' && values.adhesionAMAIRGratuite && (
              <div className="bg-white rounded-md p-4 border border-honey-200 bg-honey-50">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h5 className="font-semibold text-gray-900">AMAIR</h5>
                    <p className="text-sm text-gray-600">Association de la Maison de l'Apiculture de l'Île de la Réunion</p>
                    <p className="text-sm text-gray-500">Année {values.annee}</p>
                    <div className="mt-2 flex items-center">
                      <svg className="h-4 w-4 text-honey-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-xs text-honey-700 font-medium">Adhésion gratuite pour adhérent SAR</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-honey-600">
                      0,00 €
                    </p>
                    <p className="text-xs text-gray-500 line-through">50,00 €</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Total */}
            <div className="bg-indigo-50 rounded-md p-4 border-2 border-indigo-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total à payer</span>
                <span className="text-2xl font-bold text-indigo-600">
                  {getTotalMontant().toFixed(2)} €
                </span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Aucun organisme sélectionné</p>
        )}
      </div>

      {/* Choix du type de paiement - Masqué si montant = 0 */}
      {getTotalMontant() > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Mode de paiement</h4>
          
          <div className="space-y-3">
            {/* Paiement par chèque désactivé */}
            {/* <div className="flex items-start">
              <div className="flex items-center h-5">
                <Field
                  type="radio"
                  name="paiement.typePaiement"
                  id="cheque"
                  value="cheque"
                  className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                />
              </div>
              <div className="ml-3">
                <label htmlFor="cheque" className="font-medium text-gray-700">
                  Paiement par chèque
                </label>
                <p className="text-sm text-gray-500">
                  Envoyez votre chèque à l'ordre de l'organisme concerné à l'adresse qui vous sera communiquée.
                </p>
              </div>
            </div> */}

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <Field
                  type="radio"
                  name="paiement.typePaiement"
                  id="en_ligne"
                  value="en_ligne"
                  className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                />
              </div>
              <div className="ml-3">
                <label htmlFor="en_ligne" className="font-medium text-gray-700">
                  Paiement en ligne
                </label>
                <p className="text-sm text-gray-500">
                  Vous recevrez un lien de paiement sécurisé par email après validation de votre demande.
                </p>
              </div>
            </div>
          </div>

          {errors.paiement?.typePaiement && touched.paiement?.typePaiement && (
            <p className="mt-2 text-sm text-red-600">{errors.paiement.typePaiement}</p>
          )}
        </div>
      )}

      {/* Message pour adhésion gratuite */}
      {getTotalMontant() === 0 && organisme === 'AMAIR' && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Adhésion gratuite
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  En tant qu'adhérent SAR, votre adhésion à l'AMAIR est gratuite. Votre adhésion sera activée automatiquement après soumission du formulaire.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note importante */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Information importante
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Votre demande d'adhésion sera examinée par l'administrateur de l'organisme. 
                Vous recevrez une confirmation par email une fois votre demande validée.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paiement;
